import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { occasionsService } from '../services/occasionsService';
import { Occasion } from '../types';
import OccasionsTable from './OccasionsTable';
import EditOccasionModal from './EditOccasionModal';

const Dashboard: React.FC = () => {
  const { admin, logout } = useAuth();
  const [occasions, setOccasions] = useState<Occasion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingOccasion, setEditingOccasion] = useState<Occasion | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    loadOccasions();
  }, []);

  const loadOccasions = async () => {
    try {
      setLoading(true);
      const data = await occasionsService.getAllOccasions();
      setOccasions(data);
    } catch (error) {
      setError('Failed to load occasions');
      console.error('Error loading occasions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (occasion: Occasion) => {
    setEditingOccasion(occasion);
    setShowEditModal(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this occasion?')) {
      try {
        await occasionsService.deleteOccasion(id);
        setOccasions(occasions.filter(occasion => occasion.occasionId !== id));
      } catch (error) {
        setError('Failed to delete occasion');
        console.error('Error deleting occasion:', error);
      }
    }
  };

  const handleUpdate = async (updatedOccasion: Occasion) => {
    try {
      await occasionsService.updateOccasion(updatedOccasion.occasionId, updatedOccasion);
      setOccasions(occasions.map(occasion => 
        occasion.occasionId === updatedOccasion.occasionId ? updatedOccasion : occasion
      ));
      setShowEditModal(false);
      setEditingOccasion(null);
    } catch (error) {
      setError('Failed to update occasion');
      console.error('Error updating occasion:', error);
    }
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setEditingOccasion(null);
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Header */}
      <header style={{
        backgroundColor: '#fff',
        padding: '1rem 2rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <h1 style={{ margin: 0, color: '#333' }}>Hadawi Dashboard</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span style={{ color: '#666' }}>Welcome, {admin?.email}</span>
          <button
            onClick={logout}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Logout
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main style={{ padding: '2rem' }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '1.5rem',
            borderBottom: '1px solid #eee',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h2 style={{ margin: 0, color: '#333' }}>Occasions Management</h2>
            <button
              onClick={loadOccasions}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Refresh
            </button>
          </div>

          {error && (
            <div style={{
              padding: '1rem',
              backgroundColor: '#f8d7da',
              color: '#721c24',
              border: '1px solid #f5c6cb',
              borderRadius: '4px',
              margin: '1rem'
            }}>
              {error}
            </div>
          )}

          {loading ? (
            <div style={{
              padding: '2rem',
              textAlign: 'center',
              color: '#666'
            }}>
              Loading occasions...
            </div>
          ) : (
            <OccasionsTable
              occasions={occasions}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onUpdate={handleUpdate}
            />
          )}
        </div>
      </main>

      {/* Edit Modal */}
      {showEditModal && editingOccasion && (
        <EditOccasionModal
          occasion={editingOccasion}
          onSave={handleUpdate}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default Dashboard;
